import { Badge, Tooltip } from '@mantine/core';
import { variants } from '@uag/react-core';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PlacementSolutionDetails } from 'app/modules/wallforming/components/setting-container/automated-placement/PlacementSolutionDetails';
import { ArticleLifeCycleIcon } from 'shared/icons/ArticleLifeCycleIcon';

import type { PlacementSolution } from 'api/models';

interface PlacementSolutionRowProps {
    index: number;
    solution: PlacementSolution;
    isSelected: boolean;
    isApplied: boolean;
    hasAppliedSolution: boolean;
    handleOnSelect: (index: number) => void;
    handleOnDoubleClick: (event: React.MouseEvent, index: number) => void;
    handleOnRegisterRef: (element: HTMLDivElement | null, index: number) => void;
}

export const PlacementSolutionRow = memo(({
    index,
    solution,
    isSelected,
    isApplied,
    hasAppliedSolution,
    handleOnSelect,
    handleOnDoubleClick,
    handleOnRegisterRef
}: PlacementSolutionRowProps) => {
    const { t } = useTranslation();

    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOnSelect(index);
            }
        },
        [handleOnSelect, index]
    );

    const placementInfoGridColumnsClass = hasAppliedSolution
        ? 'grid-cols-[auto_1.5rem_minmax(0,1fr)_minmax(4.75rem,auto)_3rem] sm:grid-cols-[auto_1.8rem_minmax(0,1fr)_minmax(5rem,auto)_3.5rem]'
        : 'grid-cols-[auto_1.5rem_minmax(0,1fr)_3rem] sm:grid-cols-[auto_1.8rem_minmax(0,1fr)_3.5rem]';

    return (
        <div
            ref={(element) => handleOnRegisterRef(element, index)}
            className="group grid min-h-10 w-full min-w-96 shrink-0 cursor-pointer grid-cols-[2.5rem_minmax(0,1fr)_auto] items-stretch overflow-hidden rounded-md border border-border-ultra-light bg-background-default text-left text-xs shadow-sm transition-colors hover:border-border-strong hover:bg-background-gray-ultra-ultra-light data-[selected=true]:border-border-information data-[selected=true]:bg-background-default sm:min-w-120 sm:grid-cols-[3rem_minmax(0,1fr)_auto]"
            data-placement-solution-index={index}
            data-selected={isSelected}
            tabIndex={-1}
            onDoubleClick={(event) => handleOnDoubleClick(event, index)}
            onKeyDown={handleOnKeyDown}
            onPointerDown={() => handleOnSelect(index)}
        >
            <div className="flex items-center justify-center border-r border-border-ultra-light px-2 py-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-information-light font-mono text-xs font-semibold text-text-accent">
                    {index + 1}
                </div>
            </div>

            <div
                className={`grid min-w-0 auto-rows-min content-center text-text-default ${placementInfoGridColumnsClass}`}
            >
                {solution.placementInfos.map((usage, usageIndex) => {
                    const displayName = usage.displayName || '-';
                    const cellBorderClass =
                        usageIndex < solution.placementInfos.length - 1 ? 'border-b border-border-ultra-light' : '';

                    return (
                        <div key={usage.articleNumber} className="contents">
                            <div
                                className={`flex min-h-8 min-w-0 items-center justify-center px-2 py-1 ${cellBorderClass}`}
                            >
                                <span className="whitespace-nowrap font-mono" title={usage.articleNumber}>
                                    {usage.articleNumber}
                                </span>
                            </div>
                            <div className={`flex min-h-8 items-center justify-center px-0 py-1 ${cellBorderClass}`}>
                                <ArticleLifeCycleIcon lifeCycleCode={usage.lifeCycleCode ?? undefined} />
                            </div>
                            <div className={`flex min-h-8 min-w-0 items-center px-2 py-1 ${cellBorderClass}`}>
                                <Tooltip label={displayName} position="top">
                                    <span className="block max-w-full truncate">{displayName}</span>
                                </Tooltip>
                            </div>
                            {hasAppliedSolution && usageIndex === 0 && (
                                <div
                                    className="relative grid"
                                    style={{
                                        gridRow: `span ${solution.placementInfos.length} / span ${solution.placementInfos.length}`
                                    }}
                                >
                                    {solution.placementInfos.map((placementInfo, placementInfoIndex) => (
                                        <div
                                            key={placementInfo.articleNumber}
                                            className={`flex min-h-8 items-center justify-center px-1 py-1 ${placementInfoIndex < solution.placementInfos.length - 1 ? 'border-b border-border-ultra-light' : ''}`}
                                        >
                                            <div className="invisible">
                                                <Badge size="sm" variant={variants.badge.light}>
                                                    {t('labelApplied')}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                    {isApplied && (
                                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-1 py-1">
                                            <Badge size="sm" variant={variants.badge.light}>
                                                {t('labelApplied')}
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                className={`flex min-h-8 items-center justify-center px-2 py-1 font-mono tabular-nums ${cellBorderClass}`}
                            >
                                {usage.count}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-center border-l border-border-ultra-light px-2 py-2">
                <PlacementSolutionDetails index={index} isApplied={isApplied} solution={solution} />
            </div>
        </div>
    );
});
PlacementSolutionRow.displayName = 'PlacementSolutionRow';
